import { useCallback, useEffect, useState } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  usePaginatedChatHistory,
  type FetchChatHistoryPage,
} from '@terra/ui-design-system';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { useChatHistoryPaginationStore } from '../store/chatHistoryPaginationStore';
import { loadChatHistoryPage } from '../store/chatHistoryStore';
import { getInitialMessages } from '../components/chat/getInitialMessages';
import { normalizeChatError } from '../utils/chatNetworkErrors';

export function useChatInstanceInitialHistory({
  chatId,
  conversationId,
  targetId,
}: {
  chatId: string;
  conversationId: string | null;
  targetId: string | undefined;
}) {
  const storedPagination = useChatHistoryPaginationStore((state) =>
    state.paginationByChatId.get(chatId),
  );
  const setHistoryPagination = useChatHistoryPaginationStore(
    (state) => state.setHistoryPagination,
  );
  const clearHistoryPagination = useChatHistoryPaginationStore(
    (state) => state.clearHistoryPagination,
  );

  const [initialMessages, setInitialMessages] = useState<
    TerraUIMessage[] | null
  >(conversationId ? null : [...getInitialMessages()]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(
    Boolean(conversationId),
  );
  const [isLoadingHistoryPagination, setIsLoadingHistoryPagination] =
    useState(false);
  const [suppressInitialHistoryMerge, setSuppressInitialHistoryMerge] =
    useState(false);
  const [historyLoadError, setHistoryLoadError] = useState<Error | null>(null);
  const [historyLoadRetryToken, setHistoryLoadRetryToken] = useState(0);

  const retryHistoryLoad = useCallback(() => {
    setHistoryLoadError(null);
    setIsLoadingHistory(true);
    setInitialMessages(null);
    setHistoryLoadRetryToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!conversationId) {
      const existingDraftChat = useChatInstancesStore
        .getState()
        .chats.get(chatId);

      setHistoryLoadError(null);
      setInitialMessages(
        existingDraftChat
          ? existingDraftChat.messages
          : [...getInitialMessages()],
      );
      setIsLoadingHistory(false);
      setIsLoadingHistoryPagination(false);
      setSuppressInitialHistoryMerge(false);

      return;
    }

    let isActive = true;
    const existingChat = useChatInstancesStore.getState().chats.get(chatId);

    if (existingChat) {
      setHistoryLoadError(null);
      setInitialMessages(existingChat.messages);
      setIsLoadingHistory(false);

      if (storedPagination) {
        setIsLoadingHistoryPagination(false);
        setSuppressInitialHistoryMerge(false);

        return () => {
          isActive = false;
        };
      }

      setIsLoadingHistoryPagination(true);
      const loadPagination = async () => {
        try {
          const chatHistory = await loadChatHistoryPage({
            threadId: chatId,
          });

          if (!isActive) {
            return;
          }

          setHistoryPagination({
            chatId,
            nextCursor: chatHistory?.nextCursor ?? null,
            hasMore: chatHistory?.hasMore ?? false,
          });
          setSuppressInitialHistoryMerge(false);
        } catch (historyError: unknown) {
          console.error(
            'Failed to load chat history pagination:',
            historyError,
          );
          if (!isActive) {
            return;
          }

          setSuppressInitialHistoryMerge(true);
        } finally {
          if (isActive) {
            setIsLoadingHistoryPagination(false);
          }
        }
      };

      void loadPagination();

      return () => {
        isActive = false;
      };
    }

    setHistoryLoadError(null);
    setIsLoadingHistory(true);
    setIsLoadingHistoryPagination(false);
    setSuppressInitialHistoryMerge(false);
    setInitialMessages(null);

    const loadHistory = async () => {
      try {
        const chatHistory = await loadChatHistoryPage({
          threadId: chatId,
        });

        if (!isActive) {
          return;
        }

        setHistoryLoadError(null);
        setInitialMessages(
          chatHistory?.history && chatHistory.history.length > 0
            ? chatHistory.history
            : [...getInitialMessages()],
        );
        setHistoryPagination({
          chatId,
          nextCursor: chatHistory?.nextCursor ?? null,
          hasMore: chatHistory?.hasMore ?? false,
        });
      } catch (historyError: unknown) {
        console.error('Failed to load chat history:', historyError);
        if (!isActive) {
          return;
        }

        setHistoryLoadError(normalizeChatError(historyError));
        setInitialMessages(null);
        clearHistoryPagination(chatId);
      } finally {
        if (isActive) {
          setIsLoadingHistory(false);
        }
      }
    };

    void loadHistory();

    return () => {
      isActive = false;
    };
  }, [
    targetId,
    chatId,
    clearHistoryPagination,
    conversationId,
    historyLoadRetryToken,
    setHistoryPagination,
    storedPagination,
  ]);

  return {
    initialMessages,
    isLoadingHistory,
    isLoadingHistoryPagination,
    suppressInitialHistoryMerge,
    historyLoadError,
    retryHistoryLoad,
    storedPagination,
    setHistoryPagination,
  };
}

export function useChatInstanceHistoryPagination({
  chatId,
  hasChatInstance,
  conversationId,
  isLoadingHistoryPagination,
  storedPagination,
  suppressInitialHistoryMerge,
  chatMessages,
  setMessages,
  setHistoryPagination,
}: {
  chatId: string;
  hasChatInstance: boolean;
  conversationId: string | null;
  isLoadingHistoryPagination: boolean;
  storedPagination:
    | {
        nextCursor: number | null;
        hasMore: boolean;
      }
    | undefined;
  suppressInitialHistoryMerge: boolean;
  chatMessages: TerraUIMessage[];
  setMessages: (messages: TerraUIMessage[]) => void;
  setHistoryPagination: (args: {
    chatId: string;
    nextCursor: number | null;
    hasMore: boolean;
  }) => void;
}) {
  const fetchChatHistoryPage = useCallback<FetchChatHistoryPage>(
    async ({ threadId, limit, cursor }) => {
      const response = await loadChatHistoryPage({
        threadId,
        limit,
        cursor,
      });

      if (!response) {
        return null;
      }

      return {
        history: response.history,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
      };
    },
    [],
  );

  const handlePaginationChange = useCallback(
    ({
      nextCursor,
      hasMore,
    }: {
      nextCursor: number | null;
      hasMore: boolean;
    }) => {
      setHistoryPagination({ chatId, nextCursor, hasMore });
    },
    [chatId, setHistoryPagination],
  );

  return usePaginatedChatHistory({
    enabled: Boolean(
      hasChatInstance && conversationId && !isLoadingHistoryPagination,
    ),
    skipInitialFetch: Boolean(storedPagination) || suppressInitialHistoryMerge,
    threadId: chatId,
    messages: chatMessages,
    setMessages,
    fetchPage: fetchChatHistoryPage,
    initialNextCursor: storedPagination?.nextCursor ?? null,
    initialHasMore: storedPagination?.hasMore ?? false,
    onPaginationChange: handlePaginationChange,
  });
}
